import { useMemo } from "react";
import { useLang } from "../context/LangContext";
import "../styles/pages/OurSolutions.css";

const ICONS = {
  submarine: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 22C6.5 22 2 17.5 2 12S6.5 2 12 2s10 4.5 10 10-4.5 10-10 10z" />
      <path d="M12 8v4l3 3" />
    </svg>
  ),
  rnd: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M9 3H5a2 2 0 0 0-2 2v4" />
      <path d="M9 21H5a2 2 0 0 1-2-2v-4" />
      <path d="M15 3h4a2 2 0 0 1 2 2v4" />
      <path d="M15 21h4a2 2 0 0 0 2-2v-4" />
      <rect x="7" y="7" width="10" height="10" rx="1" />
    </svg>
  ),
  cnc: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M9 9h6M9 12h6M9 15h4" />
    </svg>
  ),
  print3d: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 2L2 7l10 5 10-5-10-5z" />
      <path d="M2 17l10 5 10-5" />
      <path d="M2 12l10 5 10-5" />
    </svg>
  ),
  energy: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
    </svg>
  ),
  robot: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
    </svg>
  ),
  ai: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 2a10 10 0 1 0 10 10" />
      <path d="M12 6v6l4 2" />
      <circle cx="18" cy="6" r="3" />
    </svg>
  ),
  sonar: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="2" />
      <path d="M12 2a10 10 0 0 1 0 20" />
      <path d="M12 6a6 6 0 0 1 0 12" />
    </svg>
  ),
  composite: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4z" />
      <path d="M17 14v6M14 17h6" />
    </svg>
  ),
};

const PRIMARY_SOLUTIONS = [
  { key: "autonomousSystems", icon: ICONS.submarine, tone: "cyan" },
  { key: "aiNavigation", icon: ICONS.ai, tone: "violet" },
  { key: "sonarSensor", icon: ICONS.sonar, tone: "teal" },
  { key: "roboticManipulator", icon: ICONS.robot, tone: "blue" },
  { key: "energyBattery", icon: ICONS.energy, tone: "amber" },
  { key: "compositeBody", icon: ICONS.composite, tone: "indigo" },
];

const ENGINEERING_CAPABILITIES = [
  { key: "rndPrototyping", icon: ICONS.rnd, tone: "cyan" },
  { key: "cncManufacturing", icon: ICONS.cnc, tone: "blue" },
  { key: "printingSolutions", icon: ICONS.print3d, tone: "violet" },
];

function Card({ title, icon, tone = "cyan" }) {
  return (
    <article className={`os-card os-card--${tone}`} tabIndex="0">
      <div className="os-card__inner">
        <div className="os-card__icon">
          <span className="os-card__icon-ring" aria-hidden="true" />
          {icon}
        </div>
        <h3 className="os-card__title">{title}</h3>
      </div>
    </article>
  );
}

export default function OurSolutions() {
  const { dict } = useLang();
  const t = dict?.ourSolutions ?? {};

  useMemo(() => [], []);

  const primaryItems = PRIMARY_SOLUTIONS.filter(({ key }) => Boolean(t[key]));
  const capabilityItems = ENGINEERING_CAPABILITIES.filter(({ key }) =>
    Boolean(t[key])
  );

  return (
    <section className="os-section page-section" id="solutions">
      <div className="os-solutions">
        {t.osH2 ? (
          <h2 className="os-solutions__title">
            <span>{t.osH2}</span>
          </h2>
        ) : null}

        {t.osSub ? <p className="os-solutions__sub">{t.osSub}</p> : null}

        {primaryItems.length > 0 ? (
          <div className="os-group">
            {t.primaryGroupTitle ? (
              <h3 className="os-group__title">{t.primaryGroupTitle}</h3>
            ) : null}

            <div className="os-grid">
              {primaryItems.map(({ key, icon, tone }) => (
                <Card key={key} title={t[key]} icon={icon} tone={tone} />
              ))}
            </div>
          </div>
        ) : null}

        {capabilityItems.length > 0 ? (
          <div className="os-group">
            {t.capabilityGroupTitle ? (
              <h3 className="os-group__title">{t.capabilityGroupTitle}</h3>
            ) : null}

            <div className="os-grid os-grid--capabilities">
              {capabilityItems.map(({ key, icon, tone }) => (
                <Card key={key} title={t[key]} icon={icon} tone={tone} />
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}